import { getReferenceString } from '@medplum/core';
import { Appointment, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

function getAppointmentTypeName(appointment: Appointment): string {
  return appointment.appointmentType?.coding?.[0]?.display?.toLowerCase() ?? '';
}

function isProcedureAppointment(appointment: Appointment): boolean {
  return getAppointmentTypeName(appointment).endsWith('procedure');
}

export function filterProcedureAppointmentsForPatientSummary(appointments: Appointment[]): Appointment[] {
  return appointments.filter((a) => (a.status === 'booked' || a.status === 'fulfilled') && isProcedureAppointment(a));
}

export function useAppointments(patient: Patient | undefined): {
  procedures: Appointment[];
  all: Appointment[];
  loading: boolean;
} {
  const medplum = useMedplum();
  const [procedures, setProcedures] = useState<Appointment[]>([]);
  const [all, setAll] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAppointments = useCallback(async () => {
    if (!patient?.id) {
      setProcedures([]);
      setAll([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const results = await medplum.searchResources('Appointment', {
        patient: getReferenceString(patient),
        _count: 100,
        _sort: '-date',
      });
      setProcedures(filterProcedureAppointmentsForPatientSummary(results));
      setAll(results);
    } catch {
      setProcedures([]);
      setAll([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  return { procedures, all, loading };
}
